(function () {

    'use strict';

    if (typeof window === 'undefined') {

        module.exports = exportFn(
            require('./enums/metricFormatTypes.js'),
            require('../../enums/metrics.js').dataType,
            require('./metricRegistry.js'),
            require('./metricRequest.js'),
            require('./enums/dataSourceFilterGroupIds.js'),
            require('../../modules/formatter.js'),
            require('../../modules/utils/timezone.js'),
            require('lodash'),
            require('../../../lib/riq-moment.js')
        );

    } else {
        RivalIQ.module('metrics.new', function (metricsNew) {
            metricsNew.MetricFormatter = exportFn(
                RivalIQ.metrics.new.enums.MetricFormatTypes,
                RivalIQ.enums.dataType,
                metricsNew.MetricRegistry,
                metricsNew.MetricRequest,
                metricsNew.enums.DataSourceFilterGroupIds,
                RivalIQ.formatter,
                RivalIQ.utils.timezone,
                window._,
                window.moment
            );
        });
    }

    function exportFn(MetricFormatTypes, DataTypes, MetricRegistry, MetricRequest, DataSourceFilterGroupIds, formatter, timezoneUtils, _, moment) {

        function createNumberFormatOptions(canonicalizedMetric, metricRequest, dataType, metricFormatType, additionalOptions) {

            const numberFormatOptions = _.extend({
                notation: (metricFormatType === MetricFormatTypes.abbreviated ? 'compact' : 'standard'),
                compactDisplay: 'short'
            }, additionalOptions);

            const dataSourceFields = MetricRegistry.getMetadataFromDataSource(canonicalizedMetric.dataSourceId, 'fields');
            const measureFormatOptions = _.get(dataSourceFields, [canonicalizedMetric.measure.field, 'formatOptions', metricFormatType]);

            if (measureFormatOptions) {
                return _.extend(numberFormatOptions, measureFormatOptions);
            }

            if (dataType === DataTypes.NUMERIC) {
                if (metricFormatType === MetricFormatTypes.abbreviated) {
                    numberFormatOptions.maximumFractionDigits = 2;
                    numberFormatOptions.minimumFractionDigits = 1;

                    numberFormatOptions.maximumSignificantDigits = 4;
                    numberFormatOptions.minimumSignificantDigits = 1;
                } else {
                    numberFormatOptions.maximumFractionDigits = 20;
                    numberFormatOptions.maximumSignificantDigits = 20;
                }
            }

            if (dataType === DataTypes.ABSOLUTE_PERCENT) {
                if (metricFormatType === MetricFormatTypes.abbreviated) {
                    numberFormatOptions.maximumFractionDigits = 2;
                    numberFormatOptions.minimumFractionDigits = 1;

                    numberFormatOptions.maximumSignificantDigits = 4;
                    numberFormatOptions.minimumSignificantDigits = 1;
                } else {
                    numberFormatOptions.maximumFractionDigits = 5;
                    numberFormatOptions.maximumSignificantDigits = 5;
                }
            }

            if (dataType === DataTypes.CURRENCY) {
                if (metricFormatType === MetricFormatTypes.abbreviated) {
                    numberFormatOptions.maximumFractionDigits = 2;
                    numberFormatOptions.minimumFractionDigits = 0;

                    numberFormatOptions.maximumSignificantDigits = 2;
                    numberFormatOptions.minimumSignificantDigits = 1;
                } else {
                    numberFormatOptions.maximumFractionDigits = 3;
                    numberFormatOptions.maximumSignificantDigits = 20;
                }
            }

            if (MetricRequest.isDeltaAbsoluteRequest(metricRequest)) {
                numberFormatOptions.signDisplay = 'exceptZero';
            }

            return numberFormatOptions;
        }

        function createNumberFormat(canonicalizedMetric, metricRequest, dataType, metricFormatType, locale, additionalOptions) {

            const numberFormatOptions = createNumberFormatOptions(canonicalizedMetric, metricRequest, dataType, metricFormatType, additionalOptions);
            return new Intl.NumberFormat(locale, numberFormatOptions);
        }

        function isDomainLookupField(canonicalizedMetric) {
            return _.includes(['companyId', 'companySetId', 'postTagId'], canonicalizedMetric.measure.field) || canonicalizedMetric.___returns?.operator === 'dimensionValue';
        }

        function getDomainFormatter(canonicalizedMetric, metricResult) {

            let dimensionName;
            if (canonicalizedMetric.___returns?.operator === 'dimensionValue') {
                dimensionName = canonicalizedMetric.___returns?.value;
            } else {
                dimensionName = canonicalizedMetric.measure.field;
            }

            if (_.includes(['day', 'week', 'month'], dimensionName)) {
                const timezone = canonicalizedMetric.filters.timePeriod.timezone || 'UTC';
                return rawValue => {
                    return timezoneUtils.getDateInTimezone(timezone, rawValue).format('YYYY-MM-DD');
                };
            }

            if (dimensionName === 'hashtag') {
                return rawValue => `#${rawValue}`;
            }

            const domain = metricResult.calculationMetadata?.domains?.[dimensionName] || {};
            return rawValue => domain[rawValue]?.name || rawValue;
        }

        function getFormatter(metricRequest, metricResult, metricFormatType, locale) {
            locale ||= 'en-US';
            const canonicalizedMetric = MetricRequest.canonicalizeMetricRequest(metricRequest);

            const dataSourceFields = MetricRegistry.getMetadataFromDataSource(canonicalizedMetric.dataSourceId, 'fields');

            const dataType = _.get(dataSourceFields, [canonicalizedMetric.measure.field, 'dataType']);

            let formatterFunction;

            if (isDomainLookupField(canonicalizedMetric)) {
                formatterFunction = getDomainFormatter(canonicalizedMetric, metricResult);
            } else if (dataType === DataTypes.ABSOLUTE_PERCENT || metricRequest.measure.transform === 'percentOfTotal') {
                if (metricFormatType === MetricFormatTypes.csv) {
                    formatterFunction = _.identity;
                } else {
                    const numberFormat = createNumberFormat(canonicalizedMetric, metricRequest, dataType, metricFormatType, locale, {
                        style: 'percent'
                    });
                    formatterFunction = rawValue => numberFormat.format(rawValue);
                }
            } else if (MetricRequest.isDeltaRelativeRequest(metricRequest)) {
                if (metricFormatType === MetricFormatTypes.csv) {
                    formatterFunction = rawValue => (dataType === DataTypes.CURRENCY ? rawValue.value : rawValue);
                } else {
                    const numberFormat = createNumberFormat(canonicalizedMetric, metricRequest, DataTypes.ABSOLUTE_PERCENT, metricFormatType, locale, {
                        signDisplay: 'exceptZero',
                        style: 'percent'
                    });
                    formatterFunction = rawValue => (dataType === DataTypes.CURRENCY ? numberFormat.format(rawValue.value) : numberFormat.format(rawValue));
                }
            } else if (_.includes([DataTypes.STRING, DataTypes.BOOLEAN, DataTypes.OBJECT], dataType)) {
                formatterFunction = _.identity;
            } else if (_.includes([DataTypes.INTEGER, DataTypes.NUMERIC], dataType)) {
                if (metricFormatType === MetricFormatTypes.csv) {
                    formatterFunction = _.identity;
                } else {
                    const numberFormat = createNumberFormat(canonicalizedMetric, metricRequest, dataType, metricFormatType, locale);
                    formatterFunction = rawValue => {
                        if (_.isArray(rawValue)) {
                            rawValue = _.last(rawValue);
                        }
                        return _.isFinite(rawValue) ? numberFormat.format(rawValue) : '--';
                    };
                }
            } else if (dataType === DataTypes.CURRENCY) {
                if (metricFormatType === MetricFormatTypes.csv) {
                    formatterFunction = rawValue => {
                        return rawValue.value;
                    };
                } else {

                    formatterFunction = rawValue => {
                        const currency = rawValue.currency || 'USD';
                        const value = rawValue.value;

                        const numberFormat = createNumberFormat(canonicalizedMetric, metricRequest, dataType, metricFormatType, locale, {
                            style: 'currency',
                            currency: currency
                        });

                        return numberFormat.format(value);
                    };
                }
            } else if (dataType === DataTypes.DATE) {
                formatterFunction = rawValue => {

                    if (metricFormatType === MetricFormatTypes.csv) {
                        const timezone = metricRequest.filters[DataSourceFilterGroupIds.timePeriod]?.timezone || 'UTC';
                        return timezoneUtils.getDateInTimezone(timezone, rawValue);
                    }

                    return formatter.formats.date(rawValue);
                };
            }

            if (!formatterFunction) {
                throw new Error(`no formatter configured for dataType ${dataType}, metricFormatType: ${metricFormatType}`);
            }

            return formatterFunction;
        }

        // Returns results in an array of arrays. Each inner array will have length = "num free dimensions + 1".
        // The first
        function getFormattedResults(metricRequest, metricResult, metricFormatType, locale) {
            const formatterFcn = getFormatter(metricRequest, metricResult, metricFormatType, locale);

            return _.map(metricResult.data, row => {
                const formattedValue = formatterFcn(_.last(row));
                return _.dropRight(row, 1).concat([formattedValue]);
            });
        }

        function formatDateInterval(startDate, endDate, timezone = null) {
            const startDateUtc = moment.utc(startDate);
            const startDateStr = (timezone ? startDateUtc.tz(timezone) : startDateUtc).format('MMM D, YYYY');
            const endDateUtc = moment.utc(endDate);
            const endDateStr = (timezone ? endDateUtc.tz(timezone) : endDateUtc).format('MMM D, YYYY');

            if (startDateStr !== endDateStr) {
                return `${startDateStr} - ${endDateStr}`;
            } else {
                return startDateStr;
            }
        }

        return {
            getFormatter,
            getFormattedResults,
            formatDateInterval
        };
    }
})();
